import { invalidateMinicart } from "~/app/store/bffApi";
import { useAppConfig } from "~/app/config/useAppConfig";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  IconButton,
  Snackbar,
  Stack,
  styled,
  Typography,
} from "~/common/mui";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import QuoteFileUploadSection from "./QuoteFileUploadSection";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { HelpInformation } from "./HelpInformation";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  QuoteUploadState,
  type Msg,
  type PreviewQuotesData,
  type ProcessesedRows,
  type QuoteSummary,
} from "../types/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { QuoteSplitUploadPreview } from "./QuoteSplitUploadPreview";
import { buildQuotePayload, getAccountInfo } from "../utils/QuoteSplitUtils";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { downloadQuotationSplitTemplate, sendQuoteForValidation } from "../api/sendQuoteForValidation";
import { useSelector } from "react-redux";
import { selectIsEmptyCart } from "~/features/cart/minicart/minicartRedux";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import { sendData } from "../api/QuoteSplitToCart";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { requestConfirmation } from "~/common/utils/ModalService";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";

export type QuotePreviewFormValues = {
  rows: {
    quoteNumber: string;
    items: ProcessesedRows[];
  }[];
};

export const QuoteSplitDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [quoteUploadState, setQuoteUploadState] = useState(QuoteUploadState.INITIAL);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [actualQuoteDetails, setActualQuoteDetails] = useState<QuoteSummary[]>([]);
  const [entryErrorRows, setEntryErrorRows] = useState<string[]>([]);
  const [dateModifiedRows, setDateModifiedRows] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const showOverrideButton = !useSelector(selectIsEmptyCart);
  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  const formProps = useForm<QuotePreviewFormValues>({
    mode: "onChange",
    defaultValues: {
      rows: [
        {
          quoteNumber: "",
          items: [],
        },
      ],
    },
  });

  const {
    formState: { errors },
  } = formProps;

  const quoteList = useWatch({
    control: formProps.control,
    name: "rows",
  });

  useEffect(() => {
    if (hasLoadedData && isFormEmpty()) {
      setHasLoadedData(false);
      if (actualQuoteDetails.length > 0) {
        setQuoteUploadState(QuoteUploadState.INITIAL);
        setMessages([]);
        setActualQuoteDetails([]);
        setEntryErrorRows([]);
      }
    }
  }, [quoteList, hasLoadedData]);

  const handleFileSelected = async (file: File) => {
    // Abort any previous request
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setQuoteUploadState(QuoteUploadState.UPLOADING);

    try {
      const result = await sendQuoteForValidation(file, controller.signal);

      if (!result.error) {
        setEntryErrorRows(result.data.invalidQuotes);
        setQuotePreviewValues(result.data.results);
        setQuoteUploadState(QuoteUploadState.UPLOAD_SUCCESS);
        setHasLoadedData(true);
        setSnackbarOpen(true);
      } else {
        setQuoteUploadState(QuoteUploadState.ERROR);
        setHasLoadedData(false);
        setMessages([{ type: "error", label: result.error }]);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      setQuoteUploadState(QuoteUploadState.ERROR);
      setHasLoadedData(false);
      setMessages([
        {
          type: "error",
          label: t("quotation.split.upload.error.file.exception"),
        },
      ]);
    }
  };

  const cancelUpload = () => {
    abortControllerRef.current?.abort();
    setQuoteUploadState(QuoteUploadState.INITIAL);
    setHasLoadedData(false);
  };

  const { totalQuantity, totalProducts } = useMemo<{
    totalQuantity: number;
    totalProducts: number;
  }>(() => {
    let totalQuantity = 0;
    let totalProducts = 0;

    for (const quote of quoteList) {
      totalProducts += quote.items.length;

      for (const item of quote.items) {
        totalQuantity += item.quantity;
      }
    }

    return { totalQuantity, totalProducts };
  }, [quoteList]);

  const setQuotePreviewValues = (data: PreviewQuotesData[]) => {
    if (data.length == 0) {
      return;
    }
    const actualQuoteData = data.map(({ quotesDetails, quoteNumber }) => ({
      quotesDetails,
      quoteNumber,
    }));
    setActualQuoteDetails(actualQuoteData);

    formProps.reset({
      rows: data.map((row) => ({
        quoteNumber: row.quoteNumber,
        items: row.processesedRows.map((item) => ({
          lineItemId: item.lineItemId,
          quoteNumber: item.quoteNumber,
          position: item.position,
          quantity: item.quantity,
          date: new Date(item.date),
          product: item.product,
          hasDateBeenModified: item.hasDateBeenModified,
          errorType: item.errorType,
        })),
      })),
    });

    const dateModified = data.flatMap((row) =>
      row.processesedRows.filter((r) => r.hasDateBeenModified).map((item) => item.quoteNumber),
    );
    setDateModifiedRows(dateModified);
  };

  const getMessages = () => {
    if (quoteUploadState !== QuoteUploadState.ERROR && quoteUploadState !== QuoteUploadState.UPLOAD_SUCCESS) {
      return [];
    }
    return messages;
  };

  const canSeeEndCustomer = userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW);

  const isInvalid = quoteUploadState === QuoteUploadState.ERROR;
  const hasErrorMessage = isInvalid && messages.length !== 0;

  const sendAddToCart = async (override: boolean) => {
    if (override) {
      const confirmed = await requestConfirmation(({ onCancel, onConfirm }) => (
        <DeleteCartConfirmationModal onCancel={onCancel} onOk={onConfirm} />
      ));
      if (!confirmed) {
        return;
      }
    }

    try {
      setQuoteUploadState(QuoteUploadState.LOADING_CART);
      const payload = buildQuotePayload(quoteList, actualQuoteDetails);
      const response = await sendData(payload, override);

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Add to cart failed: ${response.status} ${text}`);
      } else {
        invalidateMinicart();
        window.setTimeout(() => {
          redirectToUrl(CART_PAGE);
        }, 0);
      }

      setQuoteUploadState(QuoteUploadState.SUCCESS);
    } catch {
      setQuoteUploadState(QuoteUploadState.ERROR);
    }
  };

  const quantityErrorRows = useMemo(() => {
    const rowsErrors = errors.rows;
    if (!Array.isArray(rowsErrors)) {
      return [];
    }

    const result: string[] = [];

    rowsErrors.forEach((rowError, rowIndex) => {
      const itemErrors = rowError?.items;
      if (!Array.isArray(itemErrors)) {
        return;
      }

      itemErrors.forEach((itemError) => {
        if (itemError?.quantity?.type === "quantityMatch") {
          const quoteNumber = quoteList[rowIndex]?.quoteNumber;
          if (quoteNumber) {
            result.push(quoteNumber);
          }
        }
      });
    });

    return result;
  }, [errors.rows, quoteList]);

  const doAddToCart = () => sendAddToCart(false);
  const doOverrideCart = () => sendAddToCart(true);

  function isFormEmpty() {
    return quoteList.every((quote) => Array.isArray(quote.items) && quote.items.length === 0);
  }

  const headerContent = (
    <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
      <Typography variant="headlineSmall">
        {hasLoadedData ? t("quoteSplit.previewDialog.title") : t("quoteSplit.uploadDialog.title")}
      </Typography>
      <IconButton variant="contained" color={"secondary"} size={"tiny"} onClick={onClose}>
        <CloseRoundedIcon />
      </IconButton>
    </Stack>
  );

  const footer = (
    <>
      <Button variant="text" onClick={onClose}>
        {t("quoteSplit.uploadDialog.cancel")}
      </Button>
      {hasLoadedData && (
        <Stack direction={"row"} gap={1}>
          {showOverrideButton && (
            <Button
              variant="text"
              onClick={doOverrideCart}
              disabled={webservicesDisabled || quantityErrorRows.length > 0 || isFormEmpty()}
            >
              {t("bulkUpload.buttons.override")}
            </Button>
          )}
          <Button onClick={doAddToCart} disabled={webservicesDisabled || quantityErrorRows.length > 0 || isFormEmpty()}>
            {t("contractDetailsPage.addToCart")}
          </Button>
        </Stack>
      )}
    </>
  );

  const content = hasLoadedData ? (
    <FormProvider {...formProps}>
      <QuoteSplitUploadPreview
        actualQuoteDetails={actualQuoteDetails}
        entryErrorRows={entryErrorRows}
        dateModifiedRows={dateModifiedRows}
        quantityErrorRows={quantityErrorRows}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        action={<SnackbarCloseButton onClick={() => setSnackbarOpen(false)} />}
        message={t("quotation.split.preview.snackbar", {
          totalProducts,
          totalQuantity,
        })}
      />
    </FormProvider>
  ) : (
    <Stack gap={3} alignItems={"flex-start"}>
      <Stack gap={1} width={"100%"}>
        <QuoteFileUploadSection
          state={quoteUploadState}
          onFileSelected={handleFileSelected}
          messages={getMessages()}
          cancelUpload={cancelUpload}
        />
        {hasErrorMessage &&
          messages.map((msg) => (
            <Alert color="error" icon={<InfoOutlineIcon />}>
              {t(msg.label)}
            </Alert>
          ))}
        {entryErrorRows.length > 0 && quoteUploadState !== QuoteUploadState.UPLOADING && (
          <Alert severity={"error"}>
            {t("quotation.split.preview.errorMessage.quoteNumber", {
              count: entryErrorRows.length,
              quoteNumbers: entryErrorRows.join(", "),
            })}
          </Alert>
        )}
        <Alert color="info" icon={<ErrorOutlineIcon />}>
          <Trans i18nKey={"quoteSplit.uploadDialog.infoAlertMsg"}>
            Please ensure the uploaded file contains only quotes for the selected account:
            <Typography component={"b"}>{{ account: getAccountInfo(canSeeEndCustomer) }}</Typography>
          </Trans>
        </Alert>
      </Stack>
      <Button color="secondary" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadQuotationSplitTemplate}>
        {t("quoteSplit.uploadDialog.download")}
      </Button>
      <HelpInformation />
    </Stack>
  );

  return (
    <Dialog open={true} maxWidth={hasLoadedData ? "desktop-l" : "desktop-s"}>
      <StyledDialogHeader>{headerContent}</StyledDialogHeader>
      <StyledDialogContent>{content}</StyledDialogContent>
      <StyledDialogActions>{footer}</StyledDialogActions>
    </Dialog>
  );
};

const StyledDialogHeader = styled(DialogHeader)({
  paddingBottom: "16px",
  marginBottom: "0",
});

const StyledDialogContent = styled(DialogContent)({
  paddingTop: "0",
});

const StyledDialogActions = styled(DialogActions)({
  justifyContent: "space-between",
});
